import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import Premission from '@/utils/premissions/premission.type';

// Temporarily import directly to test
import ClientCustomerMapComponent from '@/pages/Employee/ClientCustomerMap';

// Existing lazy components
const EmployeePage = lazyRouteComponent(() => import('@/pages/Employee'));
const TelesalesCustomerMapPage = lazyRouteComponent(
  () => import('@/pages/Employee/TelesalesCustomerMap'),
);
const TelesalesMappingsPage = lazyRouteComponent(
  () =>
    import(
      '@/pages/Employee/TelesalesCustomerMap/components/TelesalesMappingsFullPage'
    ),
);
const DepartmentPage = lazyRouteComponent(
  () => import('@/pages/Employee/DepartmentPage'),
);
const DepartmentDetailsPage = lazyRouteComponent(
  () => import('@/pages/Employee/DepartmentDetailsPage'),
);
const EmployeeListPage = lazyRouteComponent(
  () => import('@/pages/Employee/EmployeeListPage'),
);
const PermissionsPage = lazyRouteComponent(
  () => import('@/pages/Employee/PermissionsPage'),
);
const EmployeeProfilePage = lazyRouteComponent(
  () => import('@/pages/Employee/EmployeeProfilePage'),
);
const NotificationTargetsPage = lazyRouteComponent(
  () => import('@/pages/Employee/NotificationTargetsPage'),
);
const SupervisorPage = lazyRouteComponent(
  () => import('@/pages/Employee/SupervisorPage'),
);
const EmployeePerformanceReportPage = lazyRouteComponent(
  () => import('@/pages/Employee/EmployeePerformanceReport'),
);
// Temporarily comment out the lazy version
// const ClientCustomerMapPage = lazyRouteComponent(
//   () => import('@/pages/Employee/ClientCustomerMap'),
// );

// Preload critical routes for faster transitions
// This will fetch the component code in the background
preloadComponent(() => import('@/pages/Employee'));
preloadComponent(() => import('@/pages/Employee/DepartmentPage'));
preloadComponent(() => import('@/pages/Employee/DepartmentDetailsPage'));
preloadComponent(() => import('@/pages/Employee/EmployeeListPage'));
preloadComponent(() => import('@/pages/Employee/PermissionsPage'));
preloadComponent(() => import('@/pages/Employee/NotificationTargetsPage'));
preloadComponent(() => import('@/pages/Employee/EmployeeProfilePage'));
preloadComponent(() => import('@/pages/Employee/SupervisorPage'));
preloadComponent(() => import('@/pages/Employee/EmployeePerformanceReport'));
// preloadComponent(() => import('@/pages/Employee/ClientCustomerMap')); // Temporarily disabled
preloadComponent(() => import('@/pages/Employee/TelesalesCustomerMap'));

// Define routes with optimized components
const AdminRoutes: RouteObject[] = [
  {
    index: true,
    element: (
      <PagePremission prem={Premission.Prem_Employee_Get_ALL}>
        <EmployeePage />
      </PagePremission>
    ),
  },
  {
    path: 'departments',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Department_Master}>
        <DepartmentPage />
      </PagePremission>
    ),
  },
  {
    path: 'departments/:departmentId',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Department_Master}>
        <DepartmentDetailsPage />
      </PagePremission>
    ),
  },
  {
    path: 'employees',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Get_ALL}>
        <EmployeeListPage />
      </PagePremission>
    ),
  },
  {
    path: 'permissions',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Update_Prem}>
        <PermissionsPage />
      </PagePremission>
    ),
  },
  {
    path: 'notification-targets',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Update_Prem}>
        <NotificationTargetsPage />
      </PagePremission>
    ),
  },
  {
    path: 'employees/:id',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Get_ALL}>
        <EmployeeProfilePage />
      </PagePremission>
    ),
  },
  {
    path: 'supervisors',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Supervisor_Read}>
        <SupervisorPage />
      </PagePremission>
    ),
  },
  {
    path: 'performance-report',
    element: (
      <PagePremission prem={Premission.Prem_Employee_Get_ALL}>
        <EmployeePerformanceReportPage />
      </PagePremission>
    ),
  },
  {
    path: 'client-customer-map',
    element: (
      <PagePremission prem={Premission.Prem_EmployeeCustomerMap}>
        <ClientCustomerMapComponent />
      </PagePremission>
    ),
  },
  {
    path: 'telesales-customer-map',
    element: <Navigate to="/hr/telesales-customer-map/retail" replace />,
  },
  {
    path: 'telesales-customer-map/:type',
    element: <TelesalesCustomerMapPage />,
  },
  {
    path: 'telesales-mappings',
    element: (
      <PagePremission prem={Premission.Prem_Tele_Sales_Clients_Map_Retail_Open}>
        <TelesalesMappingsPage />
      </PagePremission>
    ),
  },
  {
    path: 'telesales-mappings/retail',
    element: (
      <PagePremission prem={Premission.Prem_Tele_Sales_Clients_Map_Retail_Open}>
        <TelesalesMappingsPage initialDepart="retail" />
      </PagePremission>
    ),
  },
  {
    path: 'telesales-mappings/wholesale',
    element: (
      <PagePremission
        prem={Premission.Prem_Tele_Sales_Clients_WholeSaleMap_Open}
      >
        <TelesalesMappingsPage initialDepart="wholesale" />
      </PagePremission>
    ),
  },
];

export default AdminRoutes;
